import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../../supabaseClient';

export default function ProviderSignupPage() {
  const navigate = useNavigate();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);

    const { error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        data: {
          full_name: fullName.trim(),
          requested_role: 'provider_owner',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    setSubmitting(false);

    if (error) {
      setError(error.message);
      return;
    }

    setMessage(
      'Account created. Check your email for verification if confirmation is enabled, then sign in to continue.'
    );

    setTimeout(() => {
      navigate('/provider/login', { replace: true });
    }, 1200);
  };

  const handleGoogleSignup = async () => {
    setSubmitting(true);
    setError(null);
    setMessage(null);

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });

    setSubmitting(false);

    if (error) {
      setError(error.message);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f7f8fa',
        display: 'grid',
        placeItems: 'center',
        padding: '2rem 1rem',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 560,
          background: '#ffffff',
          borderRadius: 20,
          padding: '2rem',
          boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
        }}
      >
        <p style={{ margin: 0, color: '#6b7280', fontSize: '0.95rem' }}>
          Join HnB as a provider
        </p>
        <h1 style={{ marginTop: '0.5rem', marginBottom: '0.75rem' }}>
          Provider signup
        </h1>
        <p style={{ marginTop: 0, color: '#4b5563', lineHeight: 1.6 }}>
          Create a provider-owner account to begin staged onboarding for business profile,
          verification, services, slots, deposit policy, and portfolio setup.
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '1rem', marginTop: '1.5rem' }}>
          <label style={{ display: 'grid', gap: '0.4rem' }}>
            <span>Full name</span>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              autoComplete="name"
              style={{
                padding: '0.9rem 1rem',
                borderRadius: 12,
                border: '1px solid #d1d5db',
                fontSize: '1rem',
              }}
            />
          </label>

          <label style={{ display: 'grid', gap: '0.4rem' }}>
            <span>Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              style={{
                padding: '0.9rem 1rem',
                borderRadius: 12,
                border: '1px solid #d1d5db',
                fontSize: '1rem',
              }}
            />
          </label>

          <label style={{ display: 'grid', gap: '0.4rem' }}>
            <span>Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              style={{
                padding: '0.9rem 1rem',
                borderRadius: 12,
                border: '1px solid #d1d5db',
                fontSize: '1rem',
              }}
            />
          </label>

          <label style={{ display: 'grid', gap: '0.4rem' }}>
            <span>Confirm password</span>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              style={{
                padding: '0.9rem 1rem',
                borderRadius: 12,
                border: '1px solid #d1d5db',
                fontSize: '1rem',
              }}
            />
          </label>

          {error ? (
            <div
              style={{
                padding: '0.85rem 1rem',
                borderRadius: 12,
                background: '#fef2f2',
                color: '#991b1b',
                border: '1px solid #fecaca',
              }}
            >
              {error}
            </div>
          ) : null}

          {message ? (
            <div
              style={{
                padding: '0.85rem 1rem',
                borderRadius: 12,
                background: '#ecfdf5',
                color: '#065f46',
                border: '1px solid #a7f3d0',
              }}
            >
              {message}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={submitting}
            style={{
              padding: '0.95rem 1rem',
              borderRadius: 12,
              border: 'none',
              background: '#111827',
              color: '#ffffff',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {submitting ? 'Creating account...' : 'Create provider account'}
          </button>
        </form>

        <button
          type="button"
          onClick={handleGoogleSignup}
          disabled={submitting}
          style={{
            marginTop: '0.75rem',
            width: '100%',
            padding: '0.95rem 1rem',
            borderRadius: 12,
            border: '1px solid #d1d5db',
            background: '#ffffff',
            color: '#111827',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Continue with Google
        </button>

        <div style={{ marginTop: '1.5rem', display: 'grid', gap: '0.75rem' }}>
          <Link to="/provider/login">Already have a provider account?</Link>
          <Link to="/signup">Customer signup</Link>
        </div>
      </div>
    </div>
  );
}